import React from 'react';
import { Chip, FONT_SIZE_SCALE, PrintColors, PrintParagraph } from './PrintElements';
import { format } from 'date-fns';
import styled from 'styled-components';
import { Grid } from '../../../app/StyleGuide';
import { sanitizeHTML } from '../../../utils/sanitizeHTML';
import useSessionTimeline from '../hooks/useSessionTimeline';
import HyfiLogo from './HyfiLogo';

const PrintHeaderContainer = styled.div<{ includeHeaderImage: boolean }>`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin: 0 ${Grid._3};

  ${({ includeHeaderImage }) =>
    includeHeaderImage &&
    `
      grid-template-columns: 2fr 3fr;
    `}
`;

const ScheduledDate = styled.h3`
  font-weight: 900;
  color: ${PrintColors.Green};
`;

const HeaderDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${Grid._2};
  text-align: center;

  h2 {
    line-height: normal;
  }

  h4 {
    line-height: normal;
  }
`;

const HeaderImage = styled.img`
  height: auto;
  object-fit: cover;
  background-color: ${PrintColors.LightGray};
  aspect-ratio: 16/9;
  border-radius: 10px;
`;

const SessionDescription = styled(Chip)`
  text-align: center;
  padding: ${Grid._2};
  margin: ${Grid._4} ${Grid._3};
  font-size: ${({ fontSize }) => FONT_SIZE_SCALE[fontSize].body};
`;

export default function PrintHeader({
  orgName,
  fontSize,
  includeHeaderImage,
}: {
  orgName: string;
  fontSize: keyof typeof FONT_SIZE_SCALE;
  includeHeaderImage: boolean;
}) {
  const { session, timeline, viewPreferenceMap } = useSessionTimeline();
  const showHeaderImage = includeHeaderImage && !!session?.image;
  return (
    <>
      <PrintHeaderContainer includeHeaderImage={showHeaderImage}>
        <HeaderDetails>
          <HyfiLogo style={{ fill: PrintColors.Green, width: '100px' }} />
          <h2>{session?.name}</h2>
          <PrintParagraph>
            {timeline.name}
            {' • '}
            {viewPreferenceMap[timeline.viewPreference as keyof typeof viewPreferenceMap]?.title}
          </PrintParagraph>

          <ScheduledDate>{session?.scheduledDate ? format(session?.scheduledDate, 'M/dd/yyyy') : ''}</ScheduledDate>
          <PrintParagraph>{orgName}</PrintParagraph>
        </HeaderDetails>
        {showHeaderImage && <HeaderImage src={session?.image} alt={session?.name} />}
      </PrintHeaderContainer>
      {session?.description && (
        <SessionDescription
          fontSize={fontSize}
          color={PrintColors.LightGreen}
          dangerouslySetInnerHTML={{ __html: sanitizeHTML(session?.description) }}
        />
      )}
    </>
  );
}
